/**
 * /dila — D.I.L.A. Design House collection page.
 *
 * Visual language: sun-soaked patina. Warm gradient washes (amber/clay/rust),
 * weathered cotton texture, soft drop shadows, generous whitespace.
 * No explicit mention of "tie-dye" or "sun-fade" — the vibe IS the language.
 *
 * Patient-process micro-copy: every product card carries a "Cure window" note
 * signaling the 2-week patient lead time without lecturing.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Loader2 } from 'lucide-react';
import api from '../services/api';
import DilaFooter from '../components/DilaFooter';

// Core-type ordering for the rack: Set -> Hoodie -> Lux Tee -> Cropped Sweats
const coreType = (p) => {
  const n = (p.name || '').toLowerCase();
  const cat = (p.category || '').toLowerCase();
  if (cat === 'accessories' || n.includes('hat') || n.includes('bucket') || n.includes('beanie') || n.includes('cap')) return { key: 'hat', rank: 4 };
  if (cat === 'suits' || n.includes('sweatsuit') || n.includes('set')) return { key: 'set', rank: 0 };
  if (n.includes('hoodie')) return { key: 'hoodie', rank: 1 };
  if (n.includes('tee')) return { key: 'tee', rank: 2 };
  if (n.includes('sweats') || cat === 'bottoms') return { key: 'sweats', rank: 3 };
  return { key: 'other', rank: 5 };
};

const colorOf = (p) => {
  // Single-colorway products encode the colorway in the title (e.g. "Set 02", "Sweats 03")
  const m = (p.name || '').match(/\b(0\d{1,2})\b/) || (p.name || '').match(/\b(\d{1,2})\b/);
  return m ? m[1].padStart(3, '0') : null;
};

const Dila = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/shopify/collections/dila-1')
      .then((r) => {
        const list = r.data?.collection?.products || r.data?.products || [];
        const sorted = [...list].sort((a, b) => {
          const ra = coreType(a).rank;
          const rb = coreType(b).rank;
          if (ra !== rb) return ra - rb;
          return (colorOf(a) || '999').localeCompare(colorOf(b) || '999');
        });
        setProducts(sorted);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);


  return (
    <div
      className="min-h-screen text-stone-900"
      style={{
        background:
          'radial-gradient(circle at 20% 10%, #f5d99a 0%, transparent 45%), radial-gradient(circle at 80% 70%, #d97757 0%, transparent 55%), linear-gradient(135deg, #f4e7d1 0%, #e8c98b 60%, #c08552 100%)',
      }}
      data-testid="dila-collection"
    >
    {/* Grain overlay */}
    <div
      className="fixed inset-0 pointer-events-none opacity-30 mix-blend-multiply"
      style={{
        backgroundImage:
          'repeating-linear-gradient(45deg, transparent 0 8px, rgba(0,0,0,0.04) 8px 9px), radial-gradient(circle at 50% 50%, transparent 60%, rgba(0,0,0,0.15) 100%)',
      }}
    />

    <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-12">
        <Link to="/matrix" className="text-[10px] tracking-[0.35em] uppercase font-black hover:opacity-60">
          ← PRSVR
        </Link>
        <div className="text-[10px] tracking-[0.35em] uppercase font-black">
          D.I.L.A. // DESIGN HOUSE
        </div>
      </div>

      <h1 className="text-6xl sm:text-8xl lg:text-[8rem] font-black uppercase leading-[0.85] tracking-tight mb-6">
        Slow-Cured<br />
        <span className="italic opacity-70">Apparel.</span>
      </h1>

      <p className="text-base sm:text-lg max-w-xl leading-relaxed mb-10 font-medium">
        Each piece is finished by hand and rested in batches. Reservations open today —
        garments dispatch once the wash is set. Patience is the texture.
      </p>

      <div className="inline-flex items-center gap-3 border-2 border-stone-900 bg-white/30 backdrop-blur px-5 py-3 mb-16">
        <Clock className="w-4 h-4" />
        <span className="text-xs tracking-[0.25em] uppercase font-black">
          Cure Window · Up To 14 Days
        </span>
      </div>

      {/* Featured editorial — Worn by Ricco Ross */}
      <Link
        to="/chila"
        className="group flex items-center justify-between gap-4 border-2 border-stone-900 bg-stone-900 text-amber-50 px-5 py-4 mb-16 hover:bg-stone-800 transition-colors"
        data-testid="dila-chila-link"
      >
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase font-black opacity-70">The Chila Edit</div>
          <div className="text-base sm:text-lg font-black uppercase tracking-tight">Worn By Ricco Ross →</div>
        </div>
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform shrink-0" />
      </Link>

      {/* Product Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
        {loading && (
          <div className="col-span-full py-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        {!loading && products.map((p, i) => {
          const img = p.images?.[0]?.url || p.image;
          const ct = coreType(p);
          const color = colorOf(p);
          const to = color ? `/dila/${p.handle}?color=${color}` : `/dila/${p.handle}`;
          return (
            <Link
              key={p.id || p.handle || i}
              to={to}
              className="group block"
              data-testid={`dila-product-${i}`}
            >
              <div
                className="mb-4 relative overflow-hidden shadow-[0_20px_40px_-15px_rgba(120,60,20,0.4)] bg-stone-200"
              >
                {img && (
                  <img
                    src={img}
                    alt={p.name || p.title}
                    className="w-full h-auto block transition-transform duration-700 group-hover:scale-105"
                  />
                )}
                {color && (
                  <div className="absolute top-3 left-3 bg-stone-900/85 text-amber-50 text-[10px] tracking-[0.25em] font-black px-2.5 py-1 backdrop-blur-sm">
                    WASH {color}
                  </div>
                )}
                <div className="absolute bottom-3 right-3 text-[10px] tracking-[0.3em] uppercase font-black text-white/90 drop-shadow">
                  {ct.key}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-base font-black uppercase tracking-tight group-hover:underline">
                    {p.name || p.title}
                  </div>
                  <div className="text-[10px] tracking-[0.25em] uppercase opacity-60 mt-1">
                    {p.price ? `From $${parseFloat(p.price).toFixed(0)}` : 'Cure · Up to 14 days'}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform shrink-0" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Footer */}
      <DilaFooter />
    </div>
    </div>
  );
};

export default Dila;
